// PRPathways.js - Australia
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, MapPin, Users, ChevronDown, ChevronUp } from 'lucide-react';

const PRPathways = () => {
  const [expandedPathway, setExpandedPathway] = useState(null);

  const pathways = [
    {
      icon: Zap,
      title: 'General Skilled Migration (GSM)',
      subtitle: 'Skilled Independent (189), Skilled Nominated (190), Skilled Work Regional (491)',
      description: 'Points-based system for skilled workers without employer sponsorship. Most popular pathway for independent skilled migrants.',
      details: {
        overview: 'The General Skilled Migration program uses a points-based system where applicants are ranked in SkillSelect based on their points score.',
        programs: [
          {
            name: 'Skilled Independent Visa (Subclass 189)',
            requirements: 'Points-based permanent visa for skilled workers not sponsored by employer, state, or family. Requires occupation on Medium and Long-term Strategic Skills List (MLTSSL), minimum 65 points, and competent English (IELTS 6.0 equivalent).'
          },
          {
            name: 'Skilled Nominated Visa (Subclass 190)',
            requirements: 'State or territory nominated permanent visa. Requires occupation on relevant state list, state nomination (adds 5 points), minimum 65 points including nomination, and commitment to live in nominating state for 2 years.'
          },
          {
            name: 'Skilled Work Regional (Provisional) Visa (Subclass 491)',
            requirements: 'Provisional visa for regional areas (5 years). Requires state/territory nomination or family sponsorship in regional area, minimum 65 points including nomination (15 points), and commitment to live and work in regional Australia for 3 years before applying for PR (Subclass 191).'
          }
        ],
        keyPoints: [
          'Points test assesses age, English, work experience, education, and other factors',
          'Maximum points: 120+ (competitive scores typically 80-90+)',
          'Occupation must be on relevant skilled occupation list',
          'Skills assessment required from relevant assessing authority',
          'Expression of Interest (EOI) submitted through SkillSelect',
          'Invitation rounds conducted regularly (typically monthly)'
        ]
      }
    },
    {
      icon: MapPin,
      title: 'State and Territory Nomination',
      subtitle: 'NSW, Victoria, Queensland, South Australia, Western Australia, Tasmania, ACT, NT',
      description: 'State-specific programs allowing states to nominate skilled workers based on local labor market needs.',
      details: {
        overview: 'Each Australian state and territory operates its own nomination program with specific occupation lists and criteria.',
        programs: [
          {
            name: 'New South Wales (NSW)',
            requirements: 'Strong focus on Sydney and regional NSW. Occupation lists updated regularly. Requires job offer or strong connection to NSW for many streams. Competitive points scores required (typically 80-90+).'
          },
          {
            name: 'Victoria',
            requirements: 'Skilled migration program targeting occupations in demand. Graduate and skilled worker streams available. Requires job offer or Victorian qualifications for most streams. Melbourne and regional Victoria pathways.'
          },
          {
            name: 'Queensland',
            requirements: 'Skilled migration programs for Brisbane and regional Queensland. Occupation lists vary by region. Working or living in Queensland provides advantage. Strong focus on regional migration.'
          },
          {
            name: 'South Australia',
            requirements: 'Generous points system with lower score requirements (65-70 points). Strong focus on regional migration. Chain migration provisions for family members. Occupation lists regularly updated.'
          },
          {
            name: 'Western Australia',
            requirements: 'Graduate and skilled worker streams. Strong focus on occupations in demand in WA. Job offer or WA qualifications beneficial. Perth and regional WA pathways available.'
          },
          {
            name: 'Tasmania',
            requirements: 'Most accessible state nomination program. Lower points requirements. Job offer or Tasmanian qualifications provide pathways. Strong focus on attracting skilled migrants to Tasmania.'
          }
        ],
        keyPoints: [
          'State nomination adds 5 points (190) or 15 points (491)',
          'Each state has unique occupation lists and criteria',
          'Job offers or state connections often required',
          'Regional areas may have more accessible pathways',
          'Commitment to live in nominating state required',
          'Processing times vary by state (2-12 weeks for nomination)'
        ]
      }
    },
    {
      icon: Users,
      title: 'Employer Sponsored & Family',
      subtitle: 'Employer Nomination (186), Partner Visa (820/801), Parent Visa (103/143)',
      description: 'Pathways through employer sponsorship or family relationships for those who qualify.',
      details: {
        overview: 'Alternative pathways to PR through employer sponsorship or family connections in Australia.',
        programs: [
          {
            name: 'Employer Nomination Scheme (Subclass 186)',
            requirements: 'Permanent visa for skilled workers nominated by Australian employer. Requires 3 years work experience in nominated occupation, competent English, skills assessment, and employer nomination. Direct Entry or Temporary Residence Transition streams available.'
          },
          {
            name: 'Partner Visa (Subclass 820/801)',
            requirements: 'For partners of Australian citizens or PRs. Two-stage process: temporary (820) then permanent (801). Requires genuine relationship, living together (or compelling reasons), and sponsor must be settled in Australia. Processing time: 24-36 months.'
          },
          {
            name: 'Parent Visa (Subclass 103/143)',
            requirements: 'For parents of Australian citizens or PRs. Requires balance of family test (at least half of children in Australia). Contributory Parent Visa (143) has faster processing but higher cost. Non-contributory (103) has 30+ year wait times.'
          }
        ],
        keyPoints: [
          'Employer sponsorship requires approved sponsor and genuine position',
          'Partner visas require extensive relationship evidence',
          'Parent visas have long wait times and high costs',
          'No points test required for these pathways',
          'Skills assessment may still be required for employer sponsorship',
          'Medical and character requirements apply to all pathways'
        ]
      }
    }
  ];

  const togglePathway = (index) => {
    setExpandedPathway(expandedPathway === index ? null : index);
  };

  return (
    <section className="py-16 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Available PR Pathways
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Choose the pathway that best matches your profile, experience, and immigration goals.
          </p>
        </motion.div>

        {/* Pathways */}
        <div className="space-y-6">
          {pathways.map((pathway, index) => {
            const Icon = pathway.icon;
            const isExpanded = expandedPathway === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                {/* Pathway Header */}
                <button
                  onClick={() => togglePathway(index)}
                  className="w-full p-6 flex items-start gap-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-[#202356] to-[#2542D4] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={24} className="text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-[#202356] mb-2">
                      {pathway.title}
                    </h3>
                    <p className="text-sm text-gray-500 font-light mb-2">
                      {pathway.subtitle}
                    </p>
                    <p className="text-gray-600 text-sm font-light">
                      {pathway.description}
                    </p>
                  </div>

                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp size={24} className="text-[#2542D4]" />
                    ) : (
                      <ChevronDown size={24} className="text-[#2542D4]" />
                    )}
                  </div>
                </button>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 border-t border-gray-200">
                        <div className="pt-6 space-y-6">
                          {/* Overview */}
                          <div>
                            <h4 className="text-base font-semibold text-[#202356] mb-2">
                              Overview
                            </h4>
                            <p className="text-gray-600 text-sm font-light leading-relaxed">
                              {pathway.details.overview}
                            </p>
                          </div>

                          {/* Programs/Categories */}
                          <div>
                            <h4 className="text-base font-semibold text-[#202356] mb-3">
                              Programs & Requirements
                            </h4>
                            <div className="space-y-4">
                              {pathway.details.programs.map((program, pIndex) => (
                                <div key={pIndex} className="bg-[#F5F8FA] p-4 rounded-lg">
                                  <h5 className="text-sm font-semibold text-[#202356] mb-2">
                                    {program.name}
                                  </h5>
                                  <p className="text-gray-600 text-sm font-light leading-relaxed">
                                    {program.requirements}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Key Points */}
                          <div>
                            <h4 className="text-base font-semibold text-[#202356] mb-3">
                              Key Points
                            </h4>
                            <ul className="space-y-2">
                              {pathway.details.keyPoints.map((point, kIndex) => (
                                <li key={kIndex} className="flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 bg-[#2542D4] rounded-full mt-2 flex-shrink-0"></span>
                                  <span className="text-gray-600 text-sm font-light">
                                    {point}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PRPathways;
